import { PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Filter from '@/components/Filter';
import Table from '@/components/list/Table';
import { USER_STATUS, USER_TYPE } from '@/constants';
import { useAdminUsers } from '@/hooks/queries/admin';
import usePagination from '@/hooks/usePagination';
import type { GetAdminUsersParams } from '@/types/admin';

import CreateUserModal from './components/CreateUserModal';
import {
  BranchAccessLabel,
  UserStatusBadge,
  UserTypeTag,
} from './components/UserBadges';

const UserList = () => {
  const navigate = useNavigate();
  const pagination = usePagination<GetAdminUsersParams>([
    'keyword',
    'userType',
    'userStatus',
  ]);
  const query = useAdminUsers({
    ...pagination.query,
  });
  const [createModal, setCreateModal] = useState(false);

  const goDetail = (id: number) => navigate(`${PATH.USER_MANAGE.INDEX}/${id}`);

  return (
    <div css={tw`flex flex-col gap-[16px]`}>
      <Filter
        pagination={pagination}
        filters={[
          {
            name: 'keyword',
            type: 'input',
            label: '검색어',
            placeholder: '이름 또는 이메일',
          },
          {
            name: 'userType',
            type: 'select',
            label: '구분',
            placeholder: '전체',
            options: USER_TYPE,
          },
          {
            name: 'userStatus',
            type: 'select',
            label: '상태',
            placeholder: '전체',
            options: USER_STATUS,
          },
        ]}
      />

      <Table
        headerConfig={{
          showSizeSelector: true,
          showTotalCount: true,
          extra: (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModal(true)}
            >
              유저 생성
            </Button>
          ),
        }}
        columns={[
          {
            dataIndex: 'id',
            title: 'No',
            width: 70,
          },
          {
            dataIndex: 'name',
            title: '이름',
            width: 140,
          },
          {
            dataIndex: 'email',
            title: '이메일',
            width: 'auto',
          },
          {
            dataIndex: 'userType',
            title: '구분',
            width: 100,
            align: 'center',
            render: (_, { userType }) => <UserTypeTag type={userType} />,
          },
          {
            dataIndex: 'branchCount',
            title: '지점 권한',
            width: 110,
            align: 'center',
            render: (_, { hasAllBranchAccess, branchCount }) => (
              <BranchAccessLabel
                hasAllBranchAccess={hasAllBranchAccess}
                branchCount={branchCount}
              />
            ),
          },
          {
            dataIndex: 'userStatus',
            title: '상태',
            width: 100,
            render: (_, { userStatus }) => (
              <UserStatusBadge status={userStatus} />
            ),
          },
        ]}
        dataSource={query.data?.results}
        emptyText="등록된 유저가 없어요"
        onRow={(row) => ({
          onClick: () => goDetail(row.id),
        })}
        rowKey={(row) => row.id}
        pagination={{ ...pagination, total: query.data?.count }}
      />

      <CreateUserModal
        isOpen={createModal}
        onClose={() => setCreateModal(false)}
        onViewDetail={goDetail}
      />
    </div>
  );
};

export default UserList;
